import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMeContext } from '~/modules/me';
import { useResourceUserDetailsContext } from '~/modules/resource-management/resourceUserDetails/ResourceUserDetailsContext';
import { RESOURCE_USER_DETAILS_ACTION } from '~/modules/resource-management/resourceUserDetails/hooks/resourceUserDetailsAction';
import useBulkRejectUserSkillNudge from './useBulkRejectUserSkillNudge';

const useOnRejectUserNudges = ({ userUri }) => {
  const {
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();
  const intl = useIntl();
  const { dispatch } = useResourceUserDetailsContext();

  const { bulkRejectUserSkillNudge } = useBulkRejectUserSkillNudge({ userUri });

  const onRejectUserNudgesComplete = useCallback(
    ({ requestedSkills, errorCount }) => {
      if (!isPsaPraaUserProfileNudgesEnabled) return;

      const requestedSkillCount = requestedSkills.reduce(
        (acc, x) => acc + x.skillAssignments.length,
        0
      );

      if (!requestedSkillCount) return;

      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: errorCount
          ? intl.formatMessage(
              { id: 'skillInfoCard.suggestionRejectedError' },
              { count: requestedSkillCount }
            )
          : requestedSkillCount > 1
          ? intl.formatMessage({ id: 'skillInfoCard.allSuggestionsRejected' })
          : intl.formatMessage(
              { id: 'skillInfoCard.suggestionRejected' },
              { skill: requestedSkills[0].skillAssignments[0].displayText }
            )
      });
    },
    [dispatch, intl, isPsaPraaUserProfileNudgesEnabled]
  );

  const onRejectUserNudges = useCallback(
    async rejectedSkills => {
      const nudgeIds = rejectedSkills
        .flatMap(x => x.skillAssignments)
        .map(skill => skill.nudgeId);

      let errors = [];

      const {
        data: { bulkRejectUserSkillNudge: bulkRejectUserSkillNudgeData }
      } = await bulkRejectUserSkillNudge(nudgeIds);

      errors = bulkRejectUserSkillNudgeData?.errors || [];

      onRejectUserNudgesComplete({
        requestedSkills: rejectedSkills,
        errorCount: errors.length
      });
    },
    [bulkRejectUserSkillNudge, onRejectUserNudgesComplete]
  );

  return { onRejectUserNudges };
};

export default useOnRejectUserNudges;
